import { DefaultValues, Thread } from '@langchain/langgraph-sdk';
import { InfoCircleIcon, PlusIcon, XIcon } from '@langchain/untitled-ui-icons';
import { LinearProgress } from '@mui/joy';

import { useEffect, useState } from 'react';

import { useGraphThreads } from '@/Pages/Graph/src/api/useGraphSwr';
import { LoadMoreButton } from '@/components/RunsTable/LoadMoreButton';
import { Button, IconButton, Text, Tooltip } from '@/design-system';

import { useGraphAssistants } from '../src/api';
import { checkIsSystemAssistant } from '../src/components/site/Assistants/utils';

export const ThreadsList = ({
  threadId,
  setThreadId,
}: {
  threadId: string | undefined;
  setThreadId: (threadId: string | undefined) => void;
}) => {
  const { threads, isLoading, isValidating, hasMore, size, setSize } =
    useGraphThreads({});

  const [isListOpen, setIsListOpen] = useState(false);
  const { assistants } = useGraphAssistants({});

  useEffect(() => {
    if (threads?.length) {
      setIsListOpen(true);
    }
  }, [threads, setIsListOpen]);

  const getThreadName = (thread: Thread<DefaultValues>) => {
    const { values } = thread;
    const hasMessages =
      !!values &&
      typeof values === 'object' &&
      'messages' in values &&
      Array.isArray(values.messages) &&
      values.messages.length > 0;
    if (!hasMessages) return 'New Thread';
    const firstMessage = Array.isArray(values.messages) && values.messages[0];
    const { content } = firstMessage;
    if (!content) return thread.thread_id;
    if (typeof content === 'string') {
      return content.trim();
    }
    if (Array.isArray(content)) {
      const textMessages = content.filter((msg) => msg.type === 'text');
      if (textMessages.length > 0 && textMessages[0].text.trim().length > 0) {
        return textMessages[0].text.trim();
      }
    }
    return thread.thread_id;
  };

  const getThreadAgentName = (thread: Thread<DefaultValues>) => {
    const { metadata } = thread;
    const assistantId = metadata?.assistant_id as string;
    const assistant = assistants?.find((a) => a.assistant_id === assistantId);

    if (checkIsSystemAssistant(assistant)) {
      return `Default${metadata?.graph_id ? ` (${metadata?.graph_id})` : ''}`;
    }

    return assistant?.name ?? metadata?.graph_id?.toString();
  };

  if (isLoading)
    return (
      <div className="min-w-[200px]">
        <LinearProgress />
      </div>
    );

  if (!isListOpen) {
    return (
      <div className="m-4 flex flex-row gap-2">
        <Button
          onClick={() => setIsListOpen(true)}
          variant="outlined"
          color="secondary"
          size="sm"
        >
          Threads
        </Button>
        {threadId !== undefined && (
          <IconButton
            icon={PlusIcon}
            label="New Thread"
            onClick={() => setThreadId(undefined)}
            color="secondary"
            variant="outlined"
            className="bg-transparent"
            tooltipProps={{
              side: 'bottom',
            }}
          />
        )}
      </div>
    );
  }

  return (
    <div className="flex h-full w-[250px] flex-col gap-3 bg-primary p-4">
      <div className="flex flex-row items-center gap-2">
        <span className="text-sm font-semibold text-secondary">Threads</span>
        <div className="flex grow flex-row items-center justify-end gap-2">
          <Button
            onClick={() => setThreadId(undefined)}
            variant="outlined"
            color="secondary"
            size="sm"
            disabled={threadId === undefined}
            leftDecorator={PlusIcon}
          >
            New
          </Button>
          <IconButton
            icon={XIcon}
            label="Close"
            onClick={() => setIsListOpen(false)}
            color="secondary"
            variant="plain"
          />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {threads?.length ? (
          <>
            {threads?.map((thread) => (
              <Button
                key={thread.thread_id}
                onClick={() => setThreadId(thread.thread_id)}
                variant="plain"
                color="secondary"
                size="md"
                className="w-full"
              >
                <div className="flex w-full flex-row items-center justify-between gap-1">
                  <Text
                    variant="md"
                    className="line-clamp-1 truncate text-left text-xs font-medium text-secondary"
                    as="span"
                  >
                    {getThreadName(thread)}
                  </Text>
                  <Tooltip
                    title={
                      <div className="flex flex-col gap-1 text-xxs">
                        <div className="flex flex-row gap-1">
                          <span className="font-semibold">Agent:</span>
                          <span>{getThreadAgentName(thread)}</span>
                        </div>
                        <div className="flex flex-row gap-1">
                          <span className="font-semibold">Thread ID:</span>
                          <span>{thread.thread_id}</span>
                        </div>
                      </div>
                    }
                    side="left"
                  >
                    <div>
                      <InfoCircleIcon className="size-4 text-tertiary" />
                    </div>
                  </Tooltip>
                </div>
              </Button>
            ))}
            {hasMore && (
              <LoadMoreButton
                isInitialLoad
                isLoading={isValidating}
                onClick={() => setSize(size + 1)}
                className="text-sm text-secondary"
              />
            )}
          </>
        ) : (
          <div className="text-center text-sm text-secondary">
            No threads found.
          </div>
        )}
      </div>
    </div>
  );
};
